<template>
  <div class="scan-page">
    <!-- 顶部标题栏 -->
    <div class="sp-header">
      <span class="sp-title">扫码核销</span>
      <div class="sp-sub">积分兑换 / 任务到店 通用核销</div>
    </div>

    <div class="sp-body">
      <!-- ========== 无权限提示 ========== -->
      <div v-if="!canVerify" class="sp-state">
        <div class="sp-ico ico-net">
          <span class="sp-ico-txt">🔒</span>
        </div>
        <span class="sp-msg msg-net">{{ permTip }}</span>
        <span class="sp-sub-msg">核销操作仅限门店工作人员</span>
        <div class="sp-actions">
          <div class="btn btn-ghost" @click="goBack">返回</div>
        </div>
      </div>

      <!-- ========== 加载态（查询核销信息 / 核销中） ========== -->
      <div v-if="stage === 'loading' || stage === 'verifying'" class="sp-state">
        <div class="sp-spinner" />
        <span class="sp-state-txt">{{ stage === 'verifying' ? '核销中…' : '正在查询核销信息…' }}</span>
      </div>

      <!-- ========== 结果页 ========== -->
      <div v-else-if="stage === 'result' && result" class="sp-state">
        <div class="sp-ico" :class="'ico-' + result.type">
          <span class="sp-ico-txt">{{ resultIcon(result.type) }}</span>
        </div>
        <span class="sp-msg" :class="'msg-' + result.type">{{ result.msg }}</span>

        <!-- 信息卡（成功/部分核销） -->
        <div v-if="result.showInfo" class="sp-info-card">
          <div v-if="result.userName" class="sp-info-row">
            <span class="sp-info-label">用户</span>
            <span class="sp-info-val">{{ result.userName }}</span>
          </div>
          <div v-if="result.taskName" class="sp-info-row">
            <span class="sp-info-label">任务</span>
            <span class="sp-info-val">{{ result.taskName }}</span>
          </div>
          <div v-if="result.productName" class="sp-info-row">
            <span class="sp-info-label">商品</span>
            <span class="sp-info-val">{{ result.productName }}</span>
          </div>
          <div v-if="result.skuName" class="sp-info-row">
            <span class="sp-info-label">规格</span>
            <span class="sp-info-val">{{ result.skuName }}</span>
          </div>
          <div v-if="result.quantity" class="sp-info-row">
            <span class="sp-info-label">本次核销</span>
            <span class="sp-info-val sp-info-num">{{ result.quantity }} 件</span>
          </div>
          <div v-if="result.remainQuantity !== undefined" class="sp-info-row">
            <span class="sp-info-label">剩余待核销</span>
            <span class="sp-info-val sp-info-num">{{ result.remainQuantity }} 件</span>
          </div>
          <div v-if="result.orderNo" class="sp-info-row">
            <span class="sp-info-label">订单号</span>
            <span class="sp-info-val mono">{{ result.orderNo }}</span>
          </div>
        </div>

        <!-- 纯信息类（已核销/已过期/无效）的次提示 -->
        <span v-if="result.subMsg" class="sp-sub-msg">{{ result.subMsg }}</span>

        <div class="sp-actions">
          <div class="btn btn-gold" @click="reset">继续核销</div>
          <div class="btn btn-ghost" @click="goBack">返回</div>
        </div>
      </div>

      <!-- ========== 确认页：店员核对信息 + 选数量 + 确认 ========== -->
      <div v-else-if="stage === 'confirm' && info" class="sp-confirm">
        <div class="sp-confirm-head">
          <span class="sp-confirm-title">请确认核销信息</span>
          <span class="sp-confirm-sub">请核对商品与数量，确认无误后再核销</span>
        </div>

        <!-- 核销信息卡 -->
        <div class="sp-info-card">
          <!-- 任务到店核销（一次性核销，无数量概念） -->
          <template v-if="info.verifyType === 'task'">
            <div class="sp-info-row">
              <span class="sp-info-label">用户</span>
              <span class="sp-info-val">{{ info.userName || '—' }}</span>
            </div>
            <div class="sp-info-row">
              <span class="sp-info-label">任务</span>
              <span class="sp-info-val">{{ info.taskName || '—' }}</span>
            </div>
            <div v-if="info.taskDesc" class="sp-info-row">
              <span class="sp-info-label">说明</span>
              <span class="sp-info-val">{{ info.taskDesc }}</span>
            </div>
            <div class="sp-info-row">
              <span class="sp-info-label">本店核销</span>
              <span class="sp-info-val" :class="{ 'sp-info-no': info.storeCitySupported === false }">
                {{ info.storeCitySupported === false ? '不支持（任务限参与城市门店）' : '支持' }}
              </span>
            </div>
          </template>
          <!-- 积分兑换核销 -->
          <template v-else>
            <div v-if="info.productName" class="sp-info-row">
              <span class="sp-info-label">商品</span>
              <span class="sp-info-val">{{ info.productName }}</span>
            </div>
            <div v-if="info.skuName" class="sp-info-row">
              <span class="sp-info-label">规格</span>
              <span class="sp-info-val">{{ info.skuName }}</span>
            </div>
            <div v-if="info.orderNo" class="sp-info-row">
              <span class="sp-info-label">订单号</span>
              <span class="sp-info-val mono">{{ info.orderNo }}</span>
            </div>
            <div v-if="info.couponAmount" class="sp-info-row">
              <span class="sp-info-label">券面额</span>
              <span class="sp-info-val sp-info-num">¥{{ info.couponAmount }}</span>
            </div>
            <div v-if="info.couponDiscount" class="sp-info-row">
              <span class="sp-info-label">折扣</span>
              <span class="sp-info-val sp-info-num">{{ info.couponDiscount * 10 }}折</span>
            </div>
            <div v-if="info.couponThreshold && info.couponThreshold > 0" class="sp-info-row">
              <span class="sp-info-label">使用门槛</span>
              <span class="sp-info-val sp-info-num">满{{ info.couponThreshold }}元可用</span>
            </div>
            <div v-if="info.validEndTime" class="sp-info-row">
              <span class="sp-info-label">有效期至</span>
              <span class="sp-info-val">{{ info.validEndTime }}</span>
            </div>
            <div v-if="info.totalQuantity > 1" class="sp-info-row">
              <span class="sp-info-label">总数量</span>
              <span class="sp-info-val sp-info-num">{{ info.totalQuantity }} 件</span>
            </div>
            <div v-if="info.totalQuantity > 1" class="sp-info-row">
              <span class="sp-info-label">已核销</span>
              <span class="sp-info-val">{{ info.verifiedQuantity }} 件</span>
            </div>
            <div class="sp-info-row">
              <span class="sp-info-label">剩余待核销</span>
              <span class="sp-info-val sp-info-num">{{ info.remainQuantity }} 件</span>
            </div>
          </template>
        </div>

        <!-- 本次核销数量（仅积分兑换，默认 1，最多剩余件数） -->
        <div v-if="info.verifyType !== 'task'" class="sp-qty-card">
          <span class="sp-qty-label">本次核销数量</span>
          <div class="sp-stepper">
            <div class="sp-step" :class="{ off: verifyQty <= 1 }" @click="changeVerifyQty(-1)">−</div>
            <div class="sp-step-num">{{ verifyQty }} 件</div>
            <div class="sp-step" :class="{ off: verifyQty >= info.remainQuantity }" @click="changeVerifyQty(1)">＋</div>
          </div>
        </div>

        <div class="sp-actions">
          <div class="btn btn-gold" @click="confirmVerify">确认核销</div>
          <div class="btn btn-ghost" @click="backToIdle">取消</div>
        </div>
      </div>

      <!-- ========== 初始：扫码主视觉 ========== -->
      <div v-else-if="canVerify" class="sp-state sp-idle">
        <div class="sp-scan-ring" @click="scanCode">
          <span class="sp-scan-ico">◎</span>
          <span class="sp-scan-label">点击扫码核销</span>
        </div>
        <span class="sp-empty-sub">扫描用户出示的核销码（积分兑换 / 任务到店通用），或手动输入核销码</span>

        <!-- 手动输码（次级入口，可展开） -->
        <div class="sp-manual-entry" @click="showManual = !showManual">
          <span class="sp-manual-txt">{{ showManual ? '收起' : '手动输入核销码' }}</span>
          <span class="sp-manual-ic">{{ showManual ? '▾' : '▸' }}</span>
        </div>
        <div v-if="showManual" class="sp-manual-box">
          <input
            v-model="manualCode"
            class="sp-input"
            type="text"
            maxlength="20"
            placeholder="粘贴或输入核销码"
            @keyup.enter="onManualQuery"
          />
          <div
            class="sp-input-btn"
            :class="{ disabled: !manualCode.trim() }"
            @click="onManualQuery"
          >查询</div>
        </div>

        <!-- 返回按钮（底部固定） -->
        <div class="sp-back-bar">
          <div class="btn btn-ghost" @click="goBack">返回</div>
        </div>
      </div>
    </div>

    <!-- ========== H5 摄像头扫码组件 ========== -->
    <H5Scanner
      :visible="showScanner"
      @success="onScanSuccess"
      @close="closeScanner"
    />
  </div>
</template>

<script>
import { Toast } from 'vant'
import { verifyByCode, getVerifyInfo, allowWriteOff } from '@/api/pointsOrder'
import { taskVerifyInfo, taskVerifyTask } from '@/api/taskCenter'
import H5Scanner from '@/components/H5Scanner.vue'

/**
 * 页面状态机：
 *   idle      初始（扫码/输码）
 *   loading   正在查询核销信息
 *   confirm   信息已查到，店员核对 + 选本次核销数量 + 确认
 *   verifying 核销提交中
 *   result    核销结果（成功/部分/已核销/过期/无效/网络）
 */
export default {
  name: 'ScanVerify',
  components: { H5Scanner },
  data() {
    return {
      stage: 'idle',
      info: null,
      result: null,
      verifyQty: 1,
      manualCode: '',
      showManual: false,
      showScanner: false,
      canVerify: false,
      permTip: '',
      fetchSeq: 0
    }
  },
  created() {
    const urlCode = this.$route.query.code ? decodeURIComponent(this.$route.query.code) : ''
    // 从首页等无 code 入口进入时，清掉上次残留的 scan_code，确保回到 idle（点击扫码核销）状态
    if (!urlCode) this.clearScanCode()
    this.checkWriteOffPerm().then(() => {
      if (urlCode && this.canVerify) this.fetchInfo(urlCode)
    })
  },
  methods: {
    /** 检查核销权限 */
    async checkWriteOffPerm() {
      try {
        const res = await allowWriteOff()
        const canWriteOff = res && res.data && res.data.canWriteOff === true
        this.canVerify = canWriteOff
        if (!canWriteOff) {
          this.permTip = '您暂无核销权限，请联系管理员开通'
        } else {
          this.permTip = ''
        }
      } catch (e) {
        this.canVerify = false
        this.permTip = '权限校验失败，请稍后重试'
      }
    },

    /** 结果图标 */
    resultIcon(t) {
      return { ok: '✓', partial: '✓', already: '✕', expired: '⏰', invalid: '✕', net: '!' }[t] || '✕'
    },

    /** 调起扫码 */
    scanCode() {
      this.showScanner = true
    },

    /** H5 扫码组件识别成功回调 */
    onScanSuccess(rawCode) {
      const code = this.parseCode(rawCode)
      if (!code) {
        Toast('未识别到核销码')
        return
      }
      this.showScanner = false
      this.fetchInfo(code)
    },

    /** 关闭扫码组件 */
    closeScanner() {
      this.showScanner = false
    },

    /** 手动输入 → 查询核销信息 */
    onManualQuery() {
      const code = this.parseCode(this.manualCode)
      if (!code) {
        Toast('请输入核销码')
        return
      }
      this.fetchInfo(code)
    },

    /**
     * 第一步：按核销码查询信息（不核销）
     * 码前缀分流：JDREDD=任务到店核销，其余（JDPV 等）=积分兑换核销
     */
    async fetchInfo(rawCode) {
      const code = this.parseCode(rawCode)
      if (!code) {
        Toast('请输入核销码')
        return
      }
      this.saveScanCode(code)
      const seq = ++this.fetchSeq
      this.stage = 'loading'
      this.result = null

      // 任务到店核销码 → 走任务中心核销
      if (this.isTaskCode(code)) {
        await this.fetchTaskInfo(code, seq)
        return
      }

      try {
        const res = await getVerifyInfo({ verifyCode: code })
        if (seq !== this.fetchSeq) return

        // 业务码非200：按后端 returnMsg 分类为失败结果
        if (res && res.code !== 200) {
          this.clearScanCode()
          this.result = this.buildFailResult(res.returnMsg || res.msg || '', res)
          this.stage = 'result'
          return
        }

        const data = (res && res.data) || res || {}
        if (!data || (!data.verifyCode && !data.productName && data.verifyStatus === undefined)) {
          this.clearScanCode()
          this.result = { type: 'invalid', msg: '核销码无效', subMsg: '请检查核销码是否输入正确', showInfo: false }
          this.stage = 'result'
          return
        }
        const norm = this.normalizeInfo(data, code)

        if (norm.verifyStatus === 3) {
          this.clearScanCode()
          this.result = { type: 'expired', msg: '核销码已过期', subMsg: '核销码已过有效期', showInfo: false }
          this.stage = 'result'
          return
        }
        if (norm.verifyStatus === 4) {
          this.clearScanCode()
          this.result = { type: 'invalid', msg: '核销码已作废', subMsg: '该核销码不可使用', showInfo: false }
          this.stage = 'result'
          return
        }
        if (norm.verifyStatus === 2 || norm.remainQuantity <= 0) {
          this.clearScanCode()
          this.result = {
            type: 'already',
            msg: '核销码已核销',
            subMsg: '该核销码已全部使用',
            showInfo: false,
            productName: norm.productName,
            skuName: norm.skuName,
            orderNo: norm.orderNo,
            quantity: norm.totalQuantity
          }
          this.stage = 'result'
          return
        }

        this.info = norm
        this.verifyQty = 1
        this.stage = 'confirm'
      } catch (e) {
        if (seq !== this.fetchSeq) return
        this.result = this.buildFailResult(this.extractErrorMsg(e), e)
        this.stage = 'result'
      }
    },

    /** 是否为任务到店核销码（JDREDD 开头） */
    isTaskCode(code) {
      return /^JDREDD/i.test(String(code || '').trim())
    },

    /** 任务到店核销：查询用户 + 任务 + 本店是否支持核销 */
    async fetchTaskInfo(code, seq) {
      try {
        const res = await taskVerifyInfo(code)
        if (seq !== this.fetchSeq) return

        if (res && res.code !== 200) {
          this.clearScanCode()
          this.result = this.buildFailResult(res.returnMsg || res.msg || '', res)
          this.stage = 'result'
          return
        }

        const d = (res && res.data) || {}
        if (!d || (!d.taskName && !d.userName && d.status === undefined)) {
          this.clearScanCode()
          this.result = { type: 'invalid', msg: '核销码无效', subMsg: '请检查核销码是否输入正确', showInfo: false }
          this.stage = 'result'
          return
        }

        if (Number(d.status) === 1) {
          this.clearScanCode()
          this.result = { type: 'already', msg: '核销码已核销', subMsg: '该任务奖励已发放', showInfo: false }
          this.stage = 'result'
          return
        }

        if (d.storeCitySupported === false) {
          this.clearScanCode()
          this.result = { type: 'invalid', msg: '本门店不支持该任务核销', subMsg: '该任务仅参与城市门店可核销', showInfo: false }
          this.stage = 'result'
          return
        }

        this.info = {
          verifyType: 'task',
          verifyCode: code,
          userName: d.userName || '',
          taskName: d.taskName || '',
          taskDesc: d.taskDesc || '',
          status: d.status,
          storeCitySupported: d.storeCitySupported !== false,
          qrUrl: d.qrUrl || ''
        }
        this.stage = 'confirm'
      } catch (e) {
        if (seq !== this.fetchSeq) return
        this.result = this.buildFailResult(this.extractErrorMsg(e), e)
        this.stage = 'result'
      }
    },

    /** 第二步：店员确认后提交核销 */
    async confirmVerify() {
      if (!this.info || this.stage !== 'confirm') return
      this.stage = 'verifying'
      const seq = ++this.fetchSeq

      // 任务到店核销：无数量概念，一次性核销，成功后自动发放奖励
      if (this.info.verifyType === 'task') {
        try {
          const res = await taskVerifyTask(this.info.verifyCode)
          if (seq !== this.fetchSeq) return

          if (res && res.code !== 200) {
            this.result = this.buildFailResult(res.returnMsg || res.msg || '', res)
            this.stage = 'result'
            return
          }

          this.clearScanCode()
          this.result = {
            type: 'ok',
            msg: '核销成功，任务奖励已发放',
            showInfo: true,
            userName: this.info.userName,
            taskName: this.info.taskName
          }
        } catch (e) {
          if (seq !== this.fetchSeq) return
          this.result = this.buildFailResult(this.extractErrorMsg(e), e)
        } finally {
          this.stage = 'result'
        }
        return
      }

      try {
        const res = await verifyByCode(this.info.verifyCode, this.verifyQty)
        if (seq !== this.fetchSeq) return

        // 业务码非200
        if (res && res.code !== 200) {
          this.result = this.buildFailResult(res.returnMsg || res.msg || '', res)
          this.stage = 'result'
          return
        }

        const data = (res && res.data) || res || {}
        const totalQty = this.toInt(data.totalQuantity, this.info.totalQuantity)
        const verifiedNow = this.toInt(data.verifiedQuantityNow, this.verifyQty)
        const totalVerified = data.verifiedQuantity !== undefined && data.verifiedQuantity !== null
          ? this.toInt(data.verifiedQuantity, this.info.verifiedQuantity + verifiedNow)
          : Math.min(totalQty, this.info.verifiedQuantity + verifiedNow)
        const remain = Math.max(0, totalQty - totalVerified)
        const isPartial = remain > 0

        if (seq !== this.fetchSeq) return
        this.clearScanCode()
        this.result = {
          type: isPartial ? 'partial' : 'ok',
          msg: data.msg || (isPartial ? '本次核销成功' : '核销成功'),
          subMsg: isPartial ? '剩余 ' + remain + ' 件未核销' : null,
          showInfo: true,
          productName: data.productName || this.info.productName,
          skuName: data.skuName || this.info.skuName,
          orderNo: data.orderNo || this.info.orderNo,
          quantity: verifiedNow,
          remainQuantity: isPartial ? remain : undefined
        }
      } catch (e) {
        if (seq !== this.fetchSeq) return
        this.result = this.buildFailResult(this.extractErrorMsg(e), e)
      } finally {
        this.stage = 'result'
      }
    },

    /** 规整核销信息 */
    normalizeInfo(d, code) {
      const total = this.toInt(d.totalQuantity != null ? d.totalQuantity : d.quantity, 1)
      const verified = this.toInt(d.verifiedQuantity, 0)
      const remainFromServer = (d.remainQuantity !== undefined && d.remainQuantity !== null)
        ? this.toInt(d.remainQuantity, -1)
        : -1
      return {
        verifyCode: d.verifyCode || code,
        productName: d.productName || d.productNameSnapshot || '',
        skuName: d.skuName || '',
        orderNo: d.orderNo || '',
        validEndTime: d.validEndTime ? this.fmtTime(d.validEndTime) : '',
        verifyStatus: d.verifyStatus != null ? d.verifyStatus : 1,
        totalQuantity: total,
        verifiedQuantity: verified,
        remainQuantity: remainFromServer >= 0 ? remainFromServer : Math.max(0, total - verified),
        couponAmount: d.couponAmount,
        couponThreshold: d.couponThreshold,
        couponDiscount: d.couponDiscount
      }
    },

    changeVerifyQty(delta) {
      const max = (this.info && this.info.remainQuantity) || 1
      const next = this.verifyQty + delta
      if (next < 1 || next > max) return
      this.verifyQty = next
    },

    /** 从错误体中提取后端 message */
    extractErrorMsg(e) {
      if (!e) return ''
      if (typeof e === 'string' || typeof e === 'number') return String(e)
      return e.msg || e.returnMsg || e.message || e.errMsg
        || (e.data && (e.data.msg || e.data.returnMsg || e.data.message)) || ''
    },

    /** 根据后端 message / HTTP 状态分类失败结果 */
    buildFailResult(msg, e) {
      const m = String(msg || '').trim()
      const code = e && typeof e === 'object' ? (e.code != null ? e.code : (e.statusCode != null ? e.statusCode : (e.status != null ? e.status : null))) : Number(e)

      if (code === 404 || /404/.test(m)) {
        return { type: 'net', msg: '核销服务暂不可用', subMsg: '核销接口未部署或地址有误，请联系管理员', showInfo: false }
      }
      if (/已核销|全部核销|已使用|已领用过/.test(m)) {
        return { type: 'already', msg: '核销码已核销', subMsg: '该核销码已全部使用', showInfo: false }
      }
      if (/作废/.test(m)) {
        return { type: 'invalid', msg: '核销码已作废', subMsg: '该核销码不可使用', showInfo: false }
      }
      if (/过期|有效期/.test(m)) {
        return { type: 'expired', msg: '核销码已过期', subMsg: '核销码已过有效期', showInfo: false }
      }
      if (/无效|不存在|不正确|错误/.test(m)) {
        return { type: 'invalid', msg: m || '核销码无效', subMsg: '请检查核销码是否输入正确', showInfo: false }
      }
      if (/request:fail|timeout|超时|网络|连接异常|abort/i.test(m)) {
        return { type: 'net', msg: '网络异常', subMsg: '请检查网络后重试', showInfo: false }
      }
      if (!m || /^5\d\d$/.test(m)) {
        return { type: 'net', msg: '核销失败，请稍后重试', subMsg: m ? '服务异常（' + m + '）' : '服务暂时异常', showInfo: false }
      }
      return { type: 'invalid', msg: m, subMsg: null, showInfo: false }
    },

    reset() {
      this.stage = 'idle'
      this.result = null
      this.info = null
      this.verifyQty = 1
      this.manualCode = ''
    },

    backToIdle() {
      this.stage = 'idle'
      this.result = null
      this.info = null
      this.verifyQty = 1
    },

    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push('/app_home')
    },

    /** 核销码暂存 key */
    saveScanCode(code) {
      if (!code) return
      try {
        localStorage.setItem('jd_scan_code', String(code))
      } catch (e) { /* ignore */ }
    },

    takeScanCode() {
      try {
        const code = localStorage.getItem('jd_scan_code') || ''
        if (code) localStorage.removeItem('jd_scan_code')
        return String(code)
      } catch (e) {
        return ''
      }
    },

    clearScanCode() {
      try {
        localStorage.removeItem('jd_scan_code')
      } catch (e) { /* ignore */ }
    },

    /** 解析码：纯码 / URL?code= / 完整链接 三种形态 */
    parseCode(raw) {
      if (!raw) return ''
      const s = String(raw).trim()
      try {
        const u = new URL(s)
        const code = u.searchParams.get('code')
        if (code) return code
      } catch (e) { /* 不是 URL */ }
      const m = s.match(/[?&]code=([^&#]+)/)
      if (m) return decodeURIComponent(m[1])
      return s
    },

    toInt(v, fallback) {
      if (fallback === undefined) fallback = 0
      const n = Number(v)
      return Number.isFinite(n) ? n : fallback
    },

    /** 时间展示：时间戳转 yyyy-MM-dd HH:mm，字符串原样 */
    fmtTime(t) {
      if (t === null || t === undefined || t === '') return ''
      if (typeof t === 'string') return t.replace('T', ' ').slice(0, 16)
      const d = new Date(t)
      if (isNaN(d.getTime())) return String(t)
      const pad = (n) => String(n).padStart(2, '0')
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    }
  }
}
</script>

<style lang="scss" scoped>
.scan-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #1A1A1A 0%, #0d0d0d 55%, #000 100%);
  display: flex;
  flex-direction: column;
}

.sp-header {
  padding: calc(30px + env(safe-area-inset-top)) 24px 20px;
  text-align: center;
}
.sp-title {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #f5e6c8;
  letter-spacing: 3px;
}
.sp-sub {
  margin-top: 5px;
  font-size: 12px;
  color: rgba(245, 230, 200, 0.55);
}

.sp-body {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 24px calc(40px + env(safe-area-inset-bottom));
  padding-top: 30px;
}

.sp-state {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.sp-state-txt {
  font-size: 14px;
  color: rgba(245, 230, 200, 0.8);
}

/* ===== 确认页 ===== */
.sp-confirm {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sp-confirm-head {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 4px;
}
.sp-confirm-title {
  font-size: 17px;
  font-weight: 700;
  color: #f5e6c8;
  letter-spacing: 1px;
}
.sp-confirm-sub {
  font-size: 12px;
  color: rgba(245, 230, 200, 0.5);
}

/* 本次核销数量卡 */
.sp-qty-card {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(201, 163, 106, 0.1);
  border: 1px solid rgba(201, 163, 106, 0.45);
  border-radius: 8px;
  padding: 12px 14px;
}
.sp-qty-label {
  font-size: 14px;
  font-weight: 600;
  color: #f5e6c8;
}
.sp-stepper {
  display: flex;
  align-items: center;
  border: 1px solid rgba(201, 163, 106, 0.5);
  border-radius: 6px;
  overflow: hidden;
}
.sp-step {
  width: 36px;
  height: 34px;
  line-height: 32px;
  text-align: center;
  font-size: 19px;
  color: #c9a36a;
  background: rgba(255, 255, 255, 0.06);

  &:active { background: rgba(201, 163, 106, 0.25); }
  &.off {
    color: rgba(245, 230, 200, 0.25);
    background: rgba(255, 255, 255, 0.03);
  }
}
.sp-step-num {
  min-width: 65px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #c9a36a;
  border-left: 1px solid rgba(201, 163, 106, 0.3);
  border-right: 1px solid rgba(201, 163, 106, 0.3);
}

/* ===== 扫码主视觉：大圆环按钮 ===== */
.sp-scan-ring {
  margin-top: 40px;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(201, 163, 106, 0.25) 0%, rgba(201, 163, 106, 0.08) 70%, transparent 100%);
  border: 2px dashed rgba(201, 163, 106, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
}
.sp-scan-ico {
  font-size: 60px;
  color: #c9a36a;
  line-height: 1;
}
.sp-scan-label {
  font-size: 14px;
  color: #c9a36a;
  font-weight: 600;
  letter-spacing: 2px;
}

.sp-empty-sub {
  font-size: 12px;
  color: rgba(245, 230, 200, 0.55);
  text-align: center;
}

/* ===== 手动输码（折叠入口）===== */
.sp-manual-entry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 20px;
  padding: 5px 14px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  border: 1px solid rgba(201, 163, 106, 0.25);
}
.sp-manual-txt {
  font-size: 12px;
  color: rgba(201, 163, 106, 0.85);
}
.sp-manual-ic {
  font-size: 10px;
  color: rgba(201, 163, 106, 0.7);
}
.sp-manual-box {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
}
.sp-input {
  flex: 1;
  height: 44px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(201, 163, 106, 0.3);
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  color: #f5e6c8;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
  outline: none;

  &::placeholder { color: rgba(245, 230, 200, 0.35); }
}
.sp-input-btn {
  flex: none;
  padding: 0 18px;
  height: 44px;
  line-height: 44px;
  background: linear-gradient(135deg, #c9a36a, #8a6d3b);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;

  &.disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(245, 230, 200, 0.4);
  }
}

/* ===== 扫码主界面：底部固定返回按钮 ===== */
.sp-idle {
  padding-bottom: 80px;
}
.sp-back-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 10px 24px calc(20px + env(safe-area-inset-bottom));
}
.sp-back-bar .btn-ghost {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  border: 1px solid #c9a36a;
  color: #8a6d3b;
  background: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 24px;
  line-height: 1;

  &:active {
    background: rgba(201, 163, 106, 0.15);
  }
}

/* ===== 结果图标（区分类型）===== */
.sp-ico {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.sp-ico-txt {
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
}
.ico-ok { background: radial-gradient(circle, #1a1a1a 0%, #000 80%); border: 1px solid rgba(201, 163, 106, 0.6); box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5); }
.ico-ok .sp-ico-txt { color: #c9a36a; }
.ico-partial { background: radial-gradient(circle, #d4a85a 0%, #8a6d3b 80%); box-shadow: 0 5px 15px rgba(138, 109, 59, 0.4); }
.ico-partial .sp-ico-txt { color: #fff; }
.ico-already { background: rgba(120, 40, 40, 0.45); border: 1px solid rgba(220, 120, 120, 0.5); }
.ico-already .sp-ico-txt { color: #e8a0a0; }
.ico-expired { background: rgba(90, 80, 40, 0.4); border: 1px solid rgba(200, 170, 90, 0.45); }
.ico-expired .sp-ico-txt { color: #d4b86a; }
.ico-invalid { background: rgba(120, 40, 40, 0.45); border: 1px solid rgba(220, 120, 120, 0.5); }
.ico-invalid .sp-ico-txt { color: #e8a0a0; }
.ico-net { background: rgba(60, 80, 100, 0.4); border: 1px solid rgba(120, 170, 200, 0.3); }
.ico-net .sp-ico-txt { color: #a8c8e0; }

/* 结果主文案 */
.sp-msg {
  font-size: 17px;
  font-weight: 700;
  text-align: center;
}
.sp-msg.msg-ok { color: #c9a36a; }
.sp-msg.msg-partial { color: #d4a85a; }
.sp-msg.msg-already,
.sp-msg.msg-invalid { color: #e8a0a0; }
.sp-msg.msg-expired { color: #d4b86a; }
.sp-msg.msg-net { color: #a8c8e0; }

.sp-sub-msg {
  font-size: 12px;
  color: rgba(245, 230, 200, 0.5);
  text-align: center;
  margin-top: -4px;
}

/* 信息卡 */
.sp-info-card {
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(201, 163, 106, 0.28);
  border-radius: 8px;
  padding: 12px 14px;
}
.sp-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  gap: 10px;
}
.sp-info-row + .sp-info-row {
  border-top: 1px dashed rgba(201, 163, 106, 0.15);
}
.sp-info-label {
  font-size: 12px;
  color: rgba(245, 230, 200, 0.5);
  flex: none;
}
.sp-info-val {
  font-size: 13px;
  color: #f5e6c8;
  text-align: right;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sp-info-num { color: #c9a36a; font-weight: 700; }
.sp-info-val.mono { font-family: 'Courier New', monospace; font-size: 11px; }
.sp-info-val.sp-info-no { color: #e8a0a0; }

/* 操作按钮 */
.sp-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

/* 转圈 */
.sp-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(201, 163, 106, 0.3);
  border-top-color: #c9a36a;
  border-radius: 50%;
  animation: spSpin 0.8s linear infinite;
}
@keyframes spSpin {
  to { transform: rotate(360deg); }
}

/* 按钮主题 */
.btn.btn-gold {
  background: linear-gradient(135deg, #c9a36a, #8a6d3b);
  color: #fff;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 0;
  text-align: center;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}
.btn.btn-ghost {
  background: rgba(255, 255, 255, 0.08);
  color: #c9a36a;
  border: 1px solid rgba(201, 163, 106, 0.35);
  border-radius: 6px;
  font-size: 14px;
  padding: 11px 0;
  text-align: center;
  cursor: pointer;
}
</style>
